import dayjs from 'dayjs'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, useParams } from 'react-router-dom'
import { cleanupRequestStatus, adminGetBillingDetails } from 'features/billingsV2Slice'
import { Box, Card, CardContent, Stack, Typography } from '@mui/joy'
import { formatMoney } from 'utils/helpers'
import { getAdminBillingsTasksHeaders } from 'utils/headers/getAdminBillingsTasksHeaders'
import { BILLING_STATUS, DATE_TIME_FORMAT } from 'utils/config'
import routes from 'routes'
import Breadcrumbs from 'components/Breadcrumbs'
import MainLayout from 'components/templates/MainLayout'
import PageLoader from 'components/PageLoader'
import EnchantedTable from 'components/tables/EnchantedTable'
import StatusChip from 'components/UI/StatusChip'
import usePagination from 'hooks/usePagination'

const AdminBillingsV2DetailsView = () => {
  const pagination = usePagination()
  const dispatch = useDispatch()
  const { billingId } = useParams()

  const { currentBilling, loading } = useSelector((s) => s.billingsV2)

  useEffect(() => {
    dispatch(adminGetBillingDetails(billingId))
    return () => dispatch(cleanupRequestStatus())
  }, [dispatch])

  if (!billingId) return <Navigate to={routes.adminBillingsV2} />

  if (loading)
    return (
      <MainLayout>
        <PageLoader />
      </MainLayout>
    )

  if (!currentBilling) return <MainLayout></MainLayout>

  return (
    <MainLayout>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Breadcrumbs
          items={[{ label: 'Rozliczenia', to: routes.adminBillingsV2 }, { label: currentBilling.lp || '?' }]}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          mb: 1,
          gap: 1,
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'start', sm: 'center' },
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}
      >
        <Typography level="h2" component="h1">
          Rozliczenie:&nbsp;
          <Typography color="primary">{currentBilling.lp}</Typography>
        </Typography>
      </Box>

      <Card orientation="horizontal" variant="outlined">
        <CardContent>
          <Stack rowGap={0.5} columnGap={3} direction="row" useFlexGap sx={{ flexWrap: 'wrap', marginBottom: 0.5 }}>
            <Typography level="body-sm">
              <Typography color="primary" fontWeight={500}>
                Rozliczenie dla:
              </Typography>{' '}
              {currentBilling.user?.firstName} {currentBilling.user?.lastName}
            </Typography>
            <Typography level="body-sm">
              <Typography color="primary" fontWeight={500}>
                Ilość zadań:
              </Typography>{' '}
              {currentBilling.tasks.length}
            </Typography>
            <Typography level="body-sm">
              <Typography color="primary" fontWeight={500}>
                Wynagrodzenie brutto:
              </Typography>{' '}
              <Typography level="body-xs">{formatMoney(currentBilling.totalAmount)}</Typography>
            </Typography>
          </Stack>
          <Stack rowGap={0.5} columnGap={3} direction="row" useFlexGap sx={{ flexWrap: 'wrap' }}>
            <Typography level="body-sm">
              <Typography color="primary" fontWeight={500}>
                Data dodania:
              </Typography>{' '}
              {dayjs(currentBilling.createdAt).format(DATE_TIME_FORMAT)}
            </Typography>
            {currentBilling.updatedAt !== currentBilling.createdAt && (
              <Typography level="body-sm">
                <Typography color="primary" fontWeight={500}>
                  Data modyfikacji:
                </Typography>{' '}
                {dayjs(currentBilling.updatedAt).format(DATE_TIME_FORMAT)}
              </Typography>
            )}
            <Typography level="body-sm">
              <Typography color="primary" fontWeight={500}>
                Status:
              </Typography>{' '}
              <StatusChip status={currentBilling.status} statusObj={BILLING_STATUS} />
            </Typography>
          </Stack>
          <Stack rowGap={0.5} columnGap={3} direction="row" useFlexGap sx={{ flexWrap: 'wrap' }}></Stack>
        </CardContent>
      </Card>

      <Typography level="body-sm">Zadania uwzględnione w rozliczeniu:</Typography>

      <EnchantedTable
        headers={getAdminBillingsTasksHeaders()}
        data={currentBilling.tasks || []}
        offset={(pagination.values.currentPage - 1) * pagination.values.perPage}
        noDataText="Brak zadań do wyświetlenia."
      />
    </MainLayout>
  )
}

export default AdminBillingsV2DetailsView
